import { API_ENDPOINTS } from '@/constants/api'
import { clearGuestLocale, getGuestLocale, setGuestLocale } from '@/helpers/guestLocale'
import { LanguageCode } from '@/helpers/languageConstants'
import { LanguageContext } from '@/helpers/LanguageContext'
import { getApiService } from '@/services/apiService'
import { ProcessedAuctionItem } from '@/types/domain/auction'
import i18n from '@/i18n'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../zustand/Zustand'

export interface LanguageContextType {
  currentLanguage: LanguageCode
  setLanguage: (language: LanguageCode) => void
  getLocalizedName: (item: ProcessedAuctionItem) => string
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, userData, setUserData } = useAuth()
  const [currentLanguage, setCurrentLanguageState] = useState<LanguageCode>(
    () => (userData?.locale as LanguageCode) || getGuestLocale()
  )

  // Sync from DB when userData.locale changes (e.g. on login or data refresh).
  // On login, also clear the guest cookie so the DB locale takes full ownership.
  useEffect(() => {
    if (userData?.locale) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentLanguageState(userData.locale as LanguageCode)
      i18n.changeLanguage(userData.locale)
      clearGuestLocale()
    }
  }, [userData?.locale])

  const setLanguage = useCallback((language: LanguageCode) => {
    setCurrentLanguageState(language)
    i18n.changeLanguage(language)
    if (isAuthenticated && userData) {
      // Update Zustand (and thus localStorage) immediately so the next reload
      // reads the correct locale without waiting for the AuthHandler network request
      setUserData({ ...userData, locale: language })
      getApiService().request(API_ENDPOINTS.USERS.LOCALE, {
        method: 'POST',
        body: JSON.stringify({ locale: language }),
        requiresAuth: true,
      }).catch(() => {})
    } else {
      // Not logged in — persist choice in a cookie for future visits
      setGuestLocale(language)
    }
  }, [isAuthenticated, userData, setUserData])

  const contextGetLocalizedName = useCallback((item: ProcessedAuctionItem) => {
    return item.names ? item.names[currentLanguage] || item.name : item.name
  }, [currentLanguage])

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, getLocalizedName: contextGetLocalizedName }}>
      {children}
    </LanguageContext.Provider>
  )
}

export default LanguageProvider
